import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, DollarSign, Briefcase, TrendingUp, Calendar, User, Package, Clock, Award } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

function UserWorkActivityDashboard() {
  const { authenticatedFetch, user, loading: authLoading } = useAuth();
  
  const [userId, setUserId] = useState('');
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Initialize with last year default and set user ID
  useEffect(() => {
    const now = new Date();
    const lastYear = new Date();
    lastYear.setFullYear(now.getFullYear() - 1);
    
    setEndDate(now.toISOString().slice(0, 16));
    setStartDate(lastYear.toISOString().slice(0, 16));
    
    // Auto-set the logged-in user's ID
    if (user?.id) {
      setUserId(user.id);
    }
  }, [user]);

  const fetchWorkActivity = useCallback(async () => {
    if (!userId.trim()) {
      setError('No user ID available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/users/${userId}/work-activity?${params.toString()}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setActivityData(data);
      
    } catch (err) {
      console.error('Error fetching work activity:', err);
      setError(err.message);
      setActivityData(null);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, userId, startDate, endDate]);

  // Auto-fetch when user ID is set and dates are initialized
  useEffect(() => {
    if (userId && startDate && endDate && !authLoading) {
      fetchWorkActivity();
    }
  }, [userId, startDate, endDate, authLoading, fetchWorkActivity]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchWorkActivity();
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const getWorkTypeColor = (index) => {
    const colors = [
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-yellow-100 text-yellow-800 border-yellow-200',
      'bg-red-100 text-red-800 border-red-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-orange-100 text-orange-800 border-orange-200',
    ];
    return colors[index % colors.length];
  };

  const getWorkTypeBarColor = (index) => {
    const colors = [
      '#8B5CF6', '#EC4899', '#3B82F6', '#10B981', 
      '#F59E0B', '#EF4444', '#6366F1', '#F97316'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                User Work Activity Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                View detailed work statistics and compensation data
              </p>
            </div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  User ID
                </label>
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Auto-filled from login"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-100"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">
                  {user?.email || 'Loading user...'}
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={loading || !userId}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span className="text-sm font-medium">Refresh Activity</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <div className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5">⚠️</div>
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading work activity</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {!activityData && !loading && !error && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Your Work Activity</h3>
              <p className="text-gray-600">Please wait while we fetch your work statistics...</p>
            </div>
          )}

          {activityData && (
            <>
              {/* User Info Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <User className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {activityData.user.username || activityData.user.email || 'User'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {activityData.user.email}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {new Date(activityData.period.start_date).toLocaleDateString()} - {' '}
                          {new Date(activityData.period.end_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{activityData.period.days} days</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Total Work Items</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        {activityData.summary.total_work_items}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-indigo-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Total Earnings</p>
                      <p className="text-3xl font-bold text-green-600 mt-2">
                        {formatCurrency(activityData.summary.total_compensation_cents)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Work Types</p>
                      <p className="text-3xl font-bold text-purple-600 mt-2">
                        {activityData.summary.unique_work_types}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Avg. per Item</p>
                      <p className="text-3xl font-bold text-blue-600 mt-2">
                        {activityData.summary.total_work_items > 0
                          ? formatCurrency(
                              activityData.summary.total_compensation_cents / 
                              activityData.summary.total_work_items
                            )
                          : '$0.00'}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Work by Type Breakdown */}
              {activityData.work_by_type && activityData.work_by_type.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Award className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Work Breakdown by Type</h3>
                  </div>

                  <div className="space-y-4">
                    {activityData.work_by_type.map((workType, index) => (
                      <div 
                        key={workType.identifier}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getWorkTypeColor(index)}`}>
                                {workType.name || workType.identifier}
                              </span>
                              <span className="text-sm font-semibold text-gray-900">
                                {workType.count} items
                              </span>
                            </div>
                            {workType.description && (
                              <p className="text-sm text-gray-600">{workType.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              {formatCurrency(workType.total_compensation_cents)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatCurrency(workType.total_compensation_cents / workType.count)} avg
                            </p>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Contribution to Total</span>
                            <span>
                              {((workType.count / activityData.summary.total_work_items) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all duration-500"
                              style={{
                                width: `${(workType.count / activityData.summary.total_work_items) * 100}%`,
                                backgroundColor: getWorkTypeBarColor(index)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activityData.work_by_type && activityData.work_by_type.length === 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No work activity found for this period</p>
                </div>
              )}
            </>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserWorkActivityDashboard;